<template>
  <div v-if="show" class="payment-receipt-confirm-modal" role="presentation">
    <div class="payment-receipt-confirm-modal__backdrop" @click="$emit('close')"></div>

    <section
      class="payment-receipt-confirm-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-receipt-confirm-modal-title"
      @click.stop
    >
      <header class="payment-receipt-confirm-modal__header">
        <div>
          <h3 id="payment-receipt-confirm-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          type="button"
          class="payment-receipt-confirm-modal__close"
          :aria-label="closeLabel"
          @click="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="payment-receipt-confirm-modal__stub">
        <span>{{ text?.create?.confirmModal?.labels?.receipt || 'Comprobante previo' }}</span>
      </div>

      <div class="payment-receipt-confirm-modal__body">
        <div class="payment-receipt-confirm-modal__top-grid">
          <section class="payment-receipt-confirm-modal__section payment-receipt-confirm-modal__section--compact">
            <h4>{{ text?.create?.confirmModal?.sections?.supplier || 'Proveedor y documento' }}</h4>
            <div class="payment-receipt-confirm-modal__grid payment-receipt-confirm-modal__grid--two">
              <div>
                <span>{{ counterpartLabel || text?.create?.confirmModal?.labels?.supplier || 'Proveedor' }}</span>
                <strong>{{ supplierName }}</strong>
              </div>
              <div>
                <span>{{ text?.create?.confirmModal?.labels?.voucherType || 'Documento' }}</span>
                <strong>{{ voucherTypeLabel }}</strong>
              </div>
            </div>
          </section>

          <section class="payment-receipt-confirm-modal__section payment-receipt-confirm-modal__section--compact">
            <h4>{{ text?.create?.confirmModal?.sections?.paymentInfo || 'Información del pago' }}</h4>
            <div class="payment-receipt-confirm-modal__grid payment-receipt-confirm-modal__grid--two">
              <div>
                <span>{{ text?.create?.confirmModal?.labels?.date || 'Fecha' }}</span>
                <strong>{{ paymentDate }}</strong>
              </div>
              <div>
                <span>{{ text?.create?.confirmModal?.labels?.paymentForm || 'Forma de pago' }}</span>
                <strong>{{ paymentForm }}</strong>
              </div>
              <div>
                <span>{{ text?.create?.confirmModal?.labels?.paymentAccount || 'Cuenta receptora' }}</span>
                <strong>{{ paymentAccount }}</strong>
              </div>
              <div>
                <span>{{ text?.create?.confirmModal?.labels?.operationNumber || 'No. operación' }}</span>
                <strong>{{ operationNumber }}</strong>
              </div>
              <div v-if="showCurrencyAndExchangeRate">
                <span>{{ text?.create?.confirmModal?.labels?.currency || 'Moneda' }}</span>
                <strong>{{ currencyLabel }}</strong>
              </div>
              <div v-if="showCurrencyAndExchangeRate">
                <span>{{ text?.create?.confirmModal?.labels?.exchangeRate || 'Tipo de cambio' }}</span>
                <strong>{{ exchangeRateLabel }}</strong>
              </div>
            </div>
          </section>
        </div>

        <section class="payment-receipt-confirm-modal__section">
          <h4>{{ text?.create?.confirmModal?.sections?.applications || 'Aplicaciones del pago' }}</h4>
          <div class="payment-receipt-confirm-modal__table-wrap">
            <table class="payment-receipt-confirm-modal__table">
              <colgroup>
                <col class="payment-receipt-confirm-modal__col payment-receipt-confirm-modal__col--folio" />
                <col class="payment-receipt-confirm-modal__col payment-receipt-confirm-modal__col--category" />
                <col class="payment-receipt-confirm-modal__col payment-receipt-confirm-modal__col--business" />
                <col class="payment-receipt-confirm-modal__col payment-receipt-confirm-modal__col--concept" />
                <col class="payment-receipt-confirm-modal__col payment-receipt-confirm-modal__col--amount" />
              </colgroup>
              <thead>
                <tr>
                  <th>{{ text?.create?.confirmModal?.table?.headers?.folio || 'Folio' }}</th>
                  <th>{{ text?.create?.confirmModal?.table?.headers?.category || 'Categoria' }}</th>
                  <th>{{ text?.create?.confirmModal?.table?.headers?.business || 'Negocio' }}</th>
                  <th>{{ text?.create?.confirmModal?.table?.headers?.concept || 'Concepto' }}</th>
                  <th class="app-text-right">{{ text?.create?.confirmModal?.table?.headers?.amount || 'Monto' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="items.length === 0">
                  <td colspan="5" class="payment-receipt-confirm-modal__empty">
                    {{ text?.create?.confirmModal?.empty || 'No hay elementos para aplicar.' }}
                  </td>
                </tr>
                <tr v-for="item in items" :key="item.id">
                  <td>{{ item.reference || '' }}</td>
                  <td>
                    <div class="payment-receipt-confirm-modal__category-stack">
                      <strong class="payment-receipt-confirm-modal__category-main">{{ item.expenseClass || '-' }}</strong>
                      <span class="payment-receipt-confirm-modal__category-sub">{{ item.category || '-' }}</span>
                    </div>
                  </td>
                  <td class="payment-receipt-confirm-modal__business-cell">{{ item.business || '' }}</td>
                  <td>{{ item.concept || '' }}</td>
                  <td class="app-text-right app-strong">{{ formatMoney(item.amount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="4" class="app-text-right">{{ text?.create?.confirmModal?.labels?.total || 'Total a registrar' }}</td>
                  <td class="app-text-right app-strong">{{ formatMoney(totalAmount) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </div>

      <footer class="payment-receipt-confirm-modal__footer">
        <button type="button" class="btn btn-light" :disabled="submitting" @click="$emit('close')">
          {{ cancelText }}
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting || items.length === 0" @click="$emit('confirm')">
          {{ confirmText }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { formatDateShort } from '@/utils/datePresentation';
import { formatCurrency } from '@/utils/numberFormat';

export default {
  name: 'ExpenseReceiptCreateConfirmSubmitModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'normal',
    },
    voucherType: {
      type: String,
      default: 'cfdi',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    supplierName: {
      type: String,
      default: '',
    },
    voucherTypeLabel: {
      type: String,
      default: '',
    },
    counterpartLabel: {
      type: String,
      default: '',
    },
    paymentInfo: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    exchangeRate: {
      type: Number,
      default: 1,
    },
  },
  emits: ['close', 'confirm'],
  computed: {
    isSubmitWithCfdi() {
      return String(this.mode || '').trim().toLowerCase() === 'cfdi';
    },
    isCfdiMode() {
      return String(this.voucherType || '').trim().toLowerCase() === 'cfdi';
    },
    modalTitle() {
      return this.text?.create?.confirmModal?.title || 'Confirmar registro de pago';
    },
    modalSubtitle() {
      return this.text?.create?.confirmModal?.subtitle
        || 'Verifica el resumen antes de confirmar el registro del pago.';
    },
    closeLabel() {
      return this.text?.create?.confirmModal?.closeAriaLabel || 'Cerrar modal';
    },
    cancelText() {
      return this.text?.create?.confirmModal?.buttons?.cancel || 'Cancelar';
    },
    confirmText() {
      if (this.isSubmitWithCfdi) {
        return this.text?.create?.confirmModal?.buttons?.confirmCfdi || 'Confirmar y timbrar';
      }

      return this.text?.create?.confirmModal?.buttons?.confirm || 'Registrar Pago';
    },
    paymentDate() {
      return formatDateShort(this.paymentInfo?.date) || '';
    },
    paymentForm() {
      return String(this.paymentInfo?.paymentFormLabel || this.paymentInfo?.paymentForm || '').trim();
    },
    paymentAccount() {
      return String(this.paymentInfo?.paymentAccountLabel || this.paymentInfo?.paymentAccount || '').trim();
    },
    operationNumber() {
      return String(this.paymentInfo?.operationNumber || '').trim();
    },
    currencyLabel() {
      return String(this.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    showCurrencyAndExchangeRate() {
      return this.currencyLabel !== 'MXN';
    },
    exchangeRateLabel() {
      const value = Number(this.exchangeRate || 1);
      if (!Number.isFinite(value) || value <= 0) {
        return '';
      }

      return String(value);
    },
  },
  methods: {
    formatMoney(amount) {
      return formatCurrency(amount, { currency: this.currency });
    },
  },
};
</script>

<style scoped>
.payment-receipt-confirm-modal {
  position: fixed;
  inset: 0;
  z-index: 2300;
}

.payment-receipt-confirm-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
}

.payment-receipt-confirm-modal__dialog {
  position: relative;
  width: min(940px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  margin: 24px auto 0;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 24%);
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 12px;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.24);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto;
}

.payment-receipt-confirm-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.28);
  background: linear-gradient(90deg, rgba(0, 112, 205, 0.11) 0%, rgba(0, 112, 205, 0.03) 75%);
}

.payment-receipt-confirm-modal__header h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
}

.payment-receipt-confirm-modal__header p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #475569;
}

.payment-receipt-confirm-modal__close {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  color: #475569;
}

.payment-receipt-confirm-modal__stub {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(248, 250, 252, 0.88);
}

.payment-receipt-confirm-modal__stub span {
  color: #334155;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
}

.payment-receipt-confirm-modal__body {
  padding: 12px 16px;
  overflow: auto;
  display: grid;
  gap: 10px;
}

.payment-receipt-confirm-modal__top-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.payment-receipt-confirm-modal__section {
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-top: 2px solid rgba(0, 112, 205, 0.36);
  border-radius: 8px;
  padding: 10px;
  display: grid;
  gap: 7px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.8) 100%);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.payment-receipt-confirm-modal__section--compact {
  align-content: start;
}

.payment-receipt-confirm-modal__section h4 {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
}

.payment-receipt-confirm-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 14px;
}

.payment-receipt-confirm-modal__grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.payment-receipt-confirm-modal__grid span {
  display: block;
  color: #55657c;
  font-size: 11px;
  margin-bottom: 2px;
  min-height: 16px;
}

.payment-receipt-confirm-modal__grid strong {
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.15;
  min-height: 22px;
}

.payment-receipt-confirm-modal__fiscal {
  border-top: 1px dashed rgba(100, 116, 139, 0.28);
  margin-top: 4px;
  padding-top: 8px;
}

.payment-receipt-confirm-modal__fiscal h5 {
  margin: 0 0 6px;
  color: #334155;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.payment-receipt-confirm-modal__table-wrap {
  overflow: auto;
}

.payment-receipt-confirm-modal__table {
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-size: 13px;
}

.payment-receipt-confirm-modal__col--folio {
  width: 14%;
}

.payment-receipt-confirm-modal__col--category {
  width: 20%;
}

.payment-receipt-confirm-modal__col--business {
  width: 22%;
}

.payment-receipt-confirm-modal__col--concept {
  width: 30%;
}

.payment-receipt-confirm-modal__col--amount {
  width: 14%;
}

.payment-receipt-confirm-modal__table th,
.payment-receipt-confirm-modal__table td {
  padding: 9px 8px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.26);
}

.payment-receipt-confirm-modal__table th {
  color: #334155;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.payment-receipt-confirm-modal__table tfoot td {
  border-bottom: 0;
  color: #0f172a;
  font-weight: 700;
}

.payment-receipt-confirm-modal__business-cell {
  white-space: pre-line;
}

.payment-receipt-confirm-modal__category-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.payment-receipt-confirm-modal__category-main {
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.payment-receipt-confirm-modal__category-sub {
  color: #64748b;
  font-size: 12px;
}

.payment-receipt-confirm-modal__empty {
  color: #64748b;
  text-align: center;
}

.payment-receipt-confirm-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 11px 18px 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(241, 245, 249, 0.88);
}

@media (max-width: 960px) {
  .payment-receipt-confirm-modal__header h3 {
    font-size: 19px;
  }

  .payment-receipt-confirm-modal__grid strong {
    font-size: 14px;
  }

  .payment-receipt-confirm-modal__top-grid {
    grid-template-columns: 1fr;
  }

  .payment-receipt-confirm-modal__grid,
  .payment-receipt-confirm-modal__grid--two {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}
</style>
